import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ActionInterface } from "../../Interface/ActionInterface";
import Http from "../../Http/Http";
import Log from "../../Log/Log";
import Translator from "../../Translation/Translator";

const KNOWN_AUTH_REASONS = [
  "no_integration",
  "integration_disconnected",
  "authorization_expired",
  "bank_connection_required",
];

function callOnSuccess(action: ActionInterface) {
  if (action.hasOwnProperty("onSuccess") === true) {
    action.onSuccess.call(null);
  }
}

function callOnError(action: ActionInterface) {
  if (action.hasOwnProperty("onError") === true) {
    action.onError.call(null);
  }
}

function* synchronizeWithPonto(action: ActionInterface): any {
  try {
    const { bankAccountId } = action.payload;
    const res = yield call(Http.post, `bankAccount/${bankAccountId}/synchronize`);

    const status = res?.data?.data?.status;

    if (status === "no_sync_needed") {
      yield put({
        type: Constants.SNACKBAR_ADD_MESSAGE,
        payload: {
          message: Translator.trans("ponto_no_sync_needed_snackbar"),
          type: "info",
        },
      });
    }

    callOnSuccess(action);
  } catch (err) {
    const httpStatus = err?.response?.status ?? null;
    const errorId = err?.response?.data?.error?.id ?? null;
    const details = err?.response?.data?.details ?? {};

    if (httpStatus === 409 && errorId === "authorization_required") {
      if (KNOWN_AUTH_REASONS.indexOf(details.reason) === -1) {
        Log.error(
          new Error(
            `pontoAuthorizationRequired: unknown reason "${details.reason}" — backend contract violation`,
          ),
          { action },
        );
        yield put({
          type: Constants.PONTO_SYNC_ERROR,
          payload: "ponto_unexpected_error",
        });
        callOnError(action);
        return;
      }
      yield put({
        type: Constants.PONTO_AUTHORIZATION_REQUIRED,
        payload: {
          authUrl: details.authUrl,
          reason: details.reason,
        },
      });
      callOnSuccess(action);
      return;
    }

    if (httpStatus === 409 && errorId === "authorization_in_progress") {
      yield put({
        type: Constants.PONTO_AUTHORIZATION_IN_PROGRESS,
        payload: {
          authUrl: details.authUrl,
        },
      });
      callOnSuccess(action);
      return;
    }

    if (httpStatus === 409 && errorId === "sync_pending") {
      callOnSuccess(action);
      return;
    }

    // any 400: the sync didn't run / didn't succeed — one generic message, no per-id dialogs
    if (httpStatus === 400) {
      Log.warn("Ponto synchronize rejected (400)", { action, errorId });
      yield put({ type: Constants.GLOBAL_PONTO_INTEGRATION_GET }); // repaint: may drop to connect button if disconnected
      yield put({
        type: Constants.PONTO_SYNC_ERROR,
        payload: "ponto_unexpected_error",
      });
      callOnError(action);
      return;
    }

    // truly unexpected (5xx, network) — keep error severity
    Log.error(err, { action });
    yield put({
      type: Constants.PONTO_SYNC_ERROR,
      payload: "ponto_unexpected_error",
    });
    callOnError(action);
  }
}

function* revokePontoAccess(action: ActionInterface): any {
  try {
    yield call(Http.post, `bankAccount/ponto-logout`);
    callOnSuccess(action);
  } catch (err) {
    const data = err && err.response ? err.response.data : null;
    const errorId = data && data.error ? data.error.id : null;

    if (errorId === "no_ponto_integration") {
      yield put({
        type: Constants.PONTO_REVOKE_ERROR,
        payload: "no_ponto_integration",
      });
      callOnError(action);
      return;
    }

    Log.error(err, { action });
    yield put({
      type: Constants.PONTO_REVOKE_ERROR,
      payload: "ponto_unexpected_error",
    });
    callOnError(action);
  }
}

export function* watchPonto() {
  yield takeEvery(Constants.GLOBAL_PONTO_REVOKE_ACCESS, revokePontoAccess);
  yield takeEvery(Constants.GLOBAL_PONTO_SYNCHRONIZE, synchronizeWithPonto);
}
